import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { grey, red, indigo } from '@mui/material/colors';

import { useReportPage } from 'shared/hooks';
import { CustomTooltip } from 'ui';

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
    paddingTop: 25,
  },
  inner: {
    position: 'relative',
  },
  topBar: {
    height: 50,
    position: 'relative',
    marginBottom: 10,
  },
  topBarScore: {
    height: 10,
    backgroundColor: indigo[400],
    position: 'absolute',
    bottom: 0,
    '&:before, &:after': {
      content: '""',
      display: 'block',
      width: 1,
      backgroundColor: indigo[400],
      position: 'absolute',
      bottom: 0,
    },
    '&:before': {
      left: 0,
      height: 20,
    },
    '&:after': {
      right: 0,
      height: 50,
    },
  },
  average: {
    display: 'block',
    backgroundColor: grey[500],
    width: 1,
    height: 35,
    position: 'absolute',
    bottom: 0,
  },
  bottomBar: {
    backgroundColor: grey[500],
    borderRadius: 10,
    height: 10,
    overflow: 'hidden',
  },
  bottomBarScore: {
    backgroundColor: grey[700],
    height: '100%',
  },
  bottomBarScoreIndicator: {
    top: 0,
    bottom: 0,
    position: 'absolute',
    width: 1,
    backgroundColor: red[700],
  },
  topBarLabel: {
    position: 'absolute',
    transform: useReportPage ? 'translate(-50%, 10px)' : 'translateX(-50%)',
    fontSize: useReportPage ? 8 : 'inherit',
  },
});

const useLegendStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  item: {
    marginTop: 10,
    marginBottom: 10,
    '&:last-child': {
      textAlign: 'right',
    },
    '& > div': {
      marginTop: 5,
      marginBottom: 5,
    },
  },
  label: {
    color: theme.palette.primary.main,
    fontSize: useReportPage ? 8 : 'inherit',
  },
  value: {
    fontSize: useReportPage ? 10 : 18,
  },
}));

const FlatScoreComparisonChart = ({
  lowest,
  average,
  highest,
  companyImpact,
  supplyChainImpact,
}) => {
  const { t } = useTranslation();
  const usePositionsStyles = makeStyles({
    labelLowest: {
      left: `${lowest}%`,
      top: '30px',
    },
    labelAverage: {
      top: '15px',
      left: `${average}%`,
    },
    labelHighest: {
      top: 0,
      left: `${highest}%`,
    },
    bottomBarScore: {
      width: `${companyImpact}%`,
    },
    bottomBarScoreIndicator: {
      left: `${companyImpact}%`,
    },
    average: {
      left: `${average}%`,
    },
    topBarScore: {
      left: `${lowest}%`,
      right: `calc(100% - ${highest}%)`,
    },
  });
  const classes = useStyles();
  const positionsClasses = usePositionsStyles();
  const legendClasses = useLegendStyles();

  const labelsClasses = {
    lowest: clsx(classes.topBarLabel, positionsClasses.labelLowest),
    average: clsx(classes.topBarLabel, positionsClasses.labelAverage),
    highest: clsx(classes.topBarLabel, positionsClasses.labelHighest),
  };
  const indicatorsClasses = {
    bottomBarScore: clsx(classes.bottomBarScore, positionsClasses.bottomBarScore),
    bottomBarScoreIndicator: clsx(
      classes.bottomBarScoreIndicator,
      positionsClasses.bottomBarScoreIndicator,
    ),
    average: clsx(classes.average, positionsClasses.average),
    topBarScore: clsx(classes.topBarScore, positionsClasses.topBarScore),
  };

  return (
    <CustomTooltip
      placement="right"
      title={
        <>
          <Typography variant="h6">{t('Impact of company sites')}</Typography>
          <Typography variant="caption">
            {t('Lowest')}: <strong>{lowest.toFixed(1)}%</strong>
          </Typography>
          <br />
          <Typography variant="caption">
            {t('Average')}: <strong>{average.toFixed(1)}%</strong>
          </Typography>
          <br />
          <Typography variant="caption">
            {t('Highest')}: <strong>{highest.toFixed(1)}%</strong>
          </Typography>
        </>
      }
      arrow
    >
      <div className={classes.wrapper}>
        <span className={labelsClasses.lowest}>{t('Lowest')}</span>
        <span className={labelsClasses.average}>{t('Avg.')}</span>
        <span className={labelsClasses.highest}>{t('Highest')}</span>
        <div className={classes.inner}>
          <div className={classes.topBar}>
            <div className={indicatorsClasses.topBarScore} />
            <span className={indicatorsClasses.average} />
          </div>
          <div className={classes.bottomBar}>
            <div className={indicatorsClasses.bottomBarScore} />
          </div>
          <span className={indicatorsClasses.bottomBarScoreIndicator} />
        </div>
        <div className={legendClasses.wrapper}>
          <div className={legendClasses.item}>
            <div className={legendClasses.label}>{t('Company sites')}</div>
            <div className={legendClasses.value}>{companyImpact}%</div>
          </div>
          <div className={legendClasses.item}>
            <div className={legendClasses.label}>{t('Supply chain')}</div>
            <div className={legendClasses.value}>{supplyChainImpact}%</div>
          </div>
        </div>
      </div>
    </CustomTooltip>
  );
};

FlatScoreComparisonChart.propTypes = {
  companyImpact: PropTypes.number.isRequired,
  supplyChainImpact: PropTypes.number.isRequired,
  lowest: PropTypes.number.isRequired,
  average: PropTypes.number.isRequired,
  highest: PropTypes.number.isRequired,
};

export default FlatScoreComparisonChart;
